import React, { useState, useEffect, useCallback } from "react";
import { useUserPreferences } from "../contexts/UserPreferencesContext";
import { useAuth } from "react-oidc-context";
import FileUploadHandler from "./FileUploadHandler";

export const ModelManagement = ({
  models,
  selectedUsers,
  isLoading: globalIsLoading,
  editingModel,
  newModelName,
  newModelCommands,
  handleCreateModel,
  handleEditModel,
  handleSaveModel,
  handleDeleteModel,
  handleAssignModelToUsers,
  setNewModelName,
  setNewModelCommands,
  setEditingModel,
  fetchWithAuth,
  translations: t,
}) => {
  const auth = useAuth();
  const [sortField, setSortField] = useState("name");
  const [sortDirection, setDirection] = useState("asc");
  const [filterText, setFilterText] = useState("");
  const [modelDocuments, setModelDocuments] = useState({});
  const [error, setError] = useState(null);
  const { theme } = useUserPreferences();
  const isDark = theme === "dark";
  const DOCS_API_URL =
    "https://1spt18lza4.execute-api.eu-central-1.amazonaws.com/api/models";

  const fetchModelDocuments = useCallback(
    async (modelId) => {
      if (!auth.isAuthenticated || !auth.user?.access_token) return;

      try {
        const response = await fetch(`${DOCS_API_URL}/${modelId}/documents`, {
          headers: {
            Authorization: `Bearer ${auth.user.access_token}`,
            Accept: "application/json",
          },
        });

        if (!response.ok)
          throw new Error(`HTTP error! status: ${response.status}`);

        const data = await response.json();
        if (!Array.isArray(data)) throw new Error("Invalid response format");

        setModelDocuments((prev) => ({ ...prev, [modelId]: data }));
        setError(null);
      } catch (error) {
        console.error("Fetch documents error:", error);
        setModelDocuments((prev) => ({ ...prev, [modelId]: [] }));
      }
    },
    [auth.isAuthenticated, auth.user?.access_token]
  );

  useEffect(() => {
    if (
      editingModel?.modelId &&
      auth.isAuthenticated &&
      auth.user?.access_token
    ) {
      fetchModelDocuments(editingModel.modelId);
    }
  }, [
    editingModel,
    auth.isAuthenticated,
    auth.user?.access_token,
    fetchModelDocuments,
  ]);

  const handleDeleteDocument = async (modelId, documentId) => {
    if (!window.confirm(t.confirmDocumentDeletion) || !auth.user?.access_token)
      return;

    try {
      const response = await fetch(
        `${DOCS_API_URL}/${modelId}/documents/${documentId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${auth.user.access_token}`,
            Accept: "application/json",
          },
        }
      );

      if (!response.ok)
        throw new Error(`Delete failed: ${response.statusText}`);

      setModelDocuments((prev) => ({
        ...prev,
        [modelId]: prev[modelId].filter((doc) => doc.id !== documentId),
      }));

      setError(t.documentDeletedSuccessfully);
      await fetchModelDocuments(modelId);
    } catch (error) {
      setError(`${t.deleteFailed}: ${error.message}`);
      await fetchModelDocuments(modelId);
    }
  };

  const sortedModels = [...models]
    .sort((a, b) => {
      const aVal = a[sortField]?.toString().toLowerCase();
      const bVal = b[sortField]?.toString().toLowerCase();
      return sortDirection === "asc"
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    })
    .filter(
      (model) =>
        model.name.toLowerCase().includes(filterText.toLowerCase()) ||
        model.commands
          .toString()
          .toLowerCase()
          .includes(filterText.toLowerCase())
    );

  // Helper function to check if model has documents
  const hasDocuments = (modelId) => {
    return modelDocuments[modelId]?.length > 0;
  };

  // Helper function to render document management section only if documents exist
  const renderDocumentSection = (model) => {
    if (!hasDocuments(model.modelId) && !editingModel) return null;

    return (
      <div className="mt-4 border-t pt-4">
        {editingModel && (
          <>
            <h4
              className={`text-sm font-medium mb-4 ${
                isDark ? "text-gray-200" : "text-gray-700"
              }`}
            >
              {t.documentManagement}
            </h4>
            <FileUploadHandler
              modelId={editingModel.modelId}
              onUploadComplete={() => fetchModelDocuments(editingModel.modelId)}
              auth={auth}
              t={t}
            />
          </>
        )}

        {hasDocuments(model.modelId) && (
          <div className="mt-4">
            <h5
              className={`text-sm font-medium mb-2 ${
                isDark ? "text-gray-300" : "text-gray-600"
              }`}
            >
              {t.uploadedDocuments}
            </h5>
            <div className="max-h-40 overflow-y-auto border rounded">
              {modelDocuments[model.modelId].map((doc, idx) => (
                <div
                  key={idx}
                  className={`flex justify-between items-center p-2 ${
                    idx !== modelDocuments[model.modelId].length - 1
                      ? isDark
                        ? "border-b border-gray-600"
                        : "border-b border-gray-200"
                      : ""
                  }`}
                >
                  <span className={isDark ? "text-gray-300" : "text-gray-700"}>
                    {doc.name}
                  </span>
                  <button
                    onClick={() => handleDeleteDocument(model.modelId, doc.id)}
                    className="text-red-500 hover:text-red-700 px-2 py-1 rounded hover:bg-red-50"
                  >
                    {t.delete}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`p-6 rounded-lg ${isDark ? "bg-gray-800" : "bg-white"}`}>
      {/* Error messages */}
      {error && (
        <div
          className={`mb-4 p-3 rounded ${
            error.includes("success")
              ? isDark
                ? "bg-green-900/50 text-green-200"
                : "bg-green-100 text-green-800"
              : isDark
              ? "bg-red-900/50 text-red-200"
              : "bg-red-100 text-red-800"
          }`}
        >
          {error}
        </div>
      )}

      {/* Header section */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2
            className={`text-xl font-semibold ${
              isDark ? "text-white" : "text-gray-900"
            }`}
          >
            {t.modelManagement}
          </h2>
          {selectedUsers.size > 0 && (
            <p className="text-sm text-blue-500 mt-1">
              {selectedUsers.size} {t.usersSelected}
            </p>
          )}
        </div>
        {/* Sort and filter controls */}
        <div className="flex gap-4">
          <select
            className={`border rounded p-2 ${
              isDark
                ? "bg-gray-700 border-gray-600 text-white hover:bg-gray-600"
                : "bg-white border-gray-300 hover:bg-gray-50"
            } transition-colors duration-200`}
            value={sortField}
            onChange={(e) => setSortField(e.target.value)}
          >
            <option value="name">
              {t.sortBy} {t.modelName}
            </option>
            <option value="commands">
              {t.sortBy} {t.commands}
            </option>
          </select>
          <button
            onClick={() => setDirection((d) => (d === "asc" ? "desc" : "asc"))}
            className={`px-3 py-1 border rounded ${
              isDark
                ? "bg-gray-700 border-gray-600 text-white hover:bg-gray-600"
                : "bg-white border-gray-300 hover:bg-gray-50"
            }`}
          >
            {sortDirection === "asc" ? "↑" : "↓"}
          </button>
          <input
            type="text"
            placeholder={`${t.filter}...`}
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            className={`border rounded p-2 ${
              isDark
                ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                : "bg-white border-gray-300"
            }`}
          />
        </div>
      </div>

      {/* Create New Model Form */}
      <div
        className={`mb-6 p-4 border rounded ${
          isDark ? "bg-gray-700 border-gray-600" : "bg-gray-50 border-gray-200"
        }`}
      >
        <h3
          className={`text-lg font-medium mb-4 ${
            isDark ? "text-white" : "text-gray-900"
          }`}
        >
          {t.createNewModel}
        </h3>
        <form onSubmit={handleCreateModel} className="space-y-4">
          <div>
            <label
              className={`block text-sm font-medium mb-1 ${
                isDark ? "text-gray-200" : "text-gray-700"
              }`}
            >
              {t.modelName}:
            </label>
            <input
              type="text"
              value={newModelName}
              onChange={(e) => setNewModelName(e.target.value)}
              className={`mt-1 p-2 border rounded w-full ${
                isDark
                  ? "bg-gray-800 border-gray-600 text-white"
                  : "bg-white border-gray-300"
              }`}
              required
            />
          </div>
          <div>
            <label
              className={`block text-sm font-medium mb-1 ${
                isDark ? "text-gray-200" : "text-gray-700"
              }`}
            >
              {t.commands}:
            </label>
            <input
              type="text"
              value={newModelCommands}
              onChange={(e) => setNewModelCommands(e.target.value)}
              className={`mt-1 p-2 border rounded w-full ${
                isDark
                  ? "bg-gray-800 border-gray-600 text-white"
                  : "bg-white border-gray-300"
              }`}
              placeholder={t.commandsPlaceholder}
              required
            />
          </div>
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={globalIsLoading}
              className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:bg-blue-300"
            >
              {t.create}
            </button>
          </div>
        </form>
      </div>

      {/* Models List */}
      <div className="space-y-4">
        {sortedModels.map((model) => (
          <div
            key={model.modelId}
            className={`p-6 rounded-lg border ${
              isDark
                ? "bg-gray-700 border-gray-600 hover:bg-gray-600"
                : "bg-white border-gray-200 hover:bg-gray-50"
            } transition-colors duration-200`}
          >
            <div className="flex justify-between items-start">
              <div>
                <h4
                  className={`text-lg font-medium ${
                    isDark ? "text-white" : "text-gray-900"
                  }`}
                >
                  {model.name}
                </h4>
                <p
                  className={`mt-2 ${
                    isDark ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  {t.commands}:{" "}
                  {Array.isArray(model.commands)
                    ? model.commands.join(", ")
                    : model.commands}
                </p>
              </div>
              <div className="flex gap-2">
                {selectedUsers.size > 0 && (
                  <button
                    onClick={() =>
                      handleAssignModelToUsers(model, Array.from(selectedUsers))
                    }
                    className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
                  >
                    {t.assignToSelected} ({selectedUsers.size})
                  </button>
                )}
                <button
                  onClick={() => handleEditModel(model)}
                  className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                >
                  {t.edit}
                </button>
                <button
                  onClick={() => handleDeleteModel(model.modelId)}
                  className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
                >
                  {t.delete}
                </button>
              </div>
            </div>

            {/* Document Management Section - conditionally rendered */}
            {renderDocumentSection(model)}
          </div>
        ))}

        {sortedModels.length === 0 && (
          <div
            className={`text-center p-4 ${
              isDark ? "text-gray-400" : "text-gray-500"
            }`}
          >
            {t.noModelsFound}
          </div>
        )}
      </div>

      {/* Edit Model Modal */}
      {editingModel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div
            className={`rounded-lg p-6 max-w-2xl w-full ${
              isDark ? "bg-gray-800" : "bg-white"
            }`}
          >
            <h3
              className={`text-xl font-semibold mb-4 ${
                isDark ? "text-white" : "text-gray-900"
              }`}
            >
              {t.edit}: {editingModel.name}
            </h3>
            <div className="space-y-4">
              <div>
                <label
                  className={`block text-sm font-medium mb-1 ${
                    isDark ? "text-gray-200" : "text-gray-700"
                  }`}
                >
                  {t.modelName}
                </label>
                <input
                  type="text"
                  value={editingModel.name}
                  onChange={(e) =>
                    setEditingModel({ ...editingModel, name: e.target.value })
                  }
                  className={`w-full p-2 border rounded ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300"
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-1 ${
                    isDark ? "text-gray-200" : "text-gray-700"
                  }`}
                >
                  {t.commands}:
                </label>
                <input
                  type="text"
                  value={
                    Array.isArray(editingModel.commands)
                      ? editingModel.commands.join(", ")
                      : editingModel.commands
                  }
                  onChange={(e) =>
                    setEditingModel({
                      ...editingModel,
                      commands: e.target.value
                        .split(",")
                        .map((cmd) => cmd.trim()),
                    })
                  }
                  className={`w-full p-2 border rounded ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300"
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-1 ${
                    isDark ? "text-gray-200" : "text-gray-700"
                  }`}
                >
                  {t.uploadedDocuments}:
                </label>
                <div className="max-h-60 overflow-y-auto border rounded p-2">
                  {modelDocuments[editingModel.modelId]?.length > 0 ? (
                    modelDocuments[editingModel.modelId].map((doc, idx) => (
                      <div
                        key={idx}
                        className={`flex justify-between items-center py-2 ${
                          idx !==
                          modelDocuments[editingModel.modelId].length - 1
                            ? "border-b"
                            : ""
                        } ${isDark ? "border-gray-600" : "border-gray-200"}`}
                      >
                        <span
                          className={isDark ? "text-gray-300" : "text-gray-700"}
                        >
                          {doc.name}
                        </span>
                        <button
                          onClick={() =>
                            handleDeleteDocument(editingModel.modelId, doc.id)
                          }
                          className="text-red-500 hover:text-red-700"
                        >
                          {t.delete}
                        </button>
                      </div>
                    ))
                  ) : (
                    <p
                      className={`text-sm ${
                        isDark ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      {t.noDocumentsUploaded}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-1 ${
                    isDark ? "text-gray-200" : "text-gray-700"
                  }`}
                >
                  {t.uploadNewDocuments}:
                </label>
                {editingModel && (
                  <FileUploadHandler
                    modelId={editingModel.modelId}
                    onUploadComplete={() => {
                      fetchModelDocuments(editingModel.modelId);
                    }}
                    auth={auth}
                    t={t}
                  />
                )}
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => handleSaveModel(editingModel)}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
              >
                {t.save}
              </button>
              <button
                onClick={() => setEditingModel(null)}
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
              >
                {t.cancel}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModelManagement;
